// src/components/SearchableSelect/SearchableSelect.jsx
import { useState, useEffect, useRef, useMemo } from 'react';
import './SearchableSelect.css';

const SearchableSelect = ({
  label,
  options = [],          // [{ id, name }]
  value,                 // selected id
  onChange,              // (id) => void
  placeholder = 'Search...',
  disabled = false,
  loading = false,
  required = false,
  error,
  emptyMsg = 'No options',
  maxResults = 10,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapRef = useRef(null);

  const selected = options.find(o => String(o.id) === String(value));

  // Display the selected name in the input when not actively searching
  useEffect(() => {
    if (!open) setQuery(selected?.name || '');
  }, [open, selected?.name]);

  // Close on outside click
  useEffect(() => {
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = q
      ? options.filter(o => o.name?.toLowerCase().includes(q))
      : options;
    return list.slice(0, maxResults);
  }, [query, options, maxResults]);

  const totalMatches = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? options.filter(o => o.name?.toLowerCase().includes(q)).length : options.length;
  }, [query, options]);

  return (
    <div className="ss-wrap" ref={wrapRef}>
      {label && (
        <label className="form-label">
          {label} {required && <span className="required">*</span>}
        </label>
      )}
      <input
        type="text"
        className={`form-input ${error ? 'error' : ''}`}
        value={query}
        disabled={disabled || loading}
        placeholder={loading ? 'Loading...' : placeholder}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => !disabled && setOpen(true)}
        autoComplete="off"
      />
      {open && !disabled && !loading && (
        <div className="ss-dropdown">
          {filtered.length === 0 ? (
            <div className="ss-empty">{emptyMsg}</div>
          ) : (
            <>
              {filtered.map(opt => (
                <button
                  type="button"
                  key={opt.id}
                  className={`ss-option ${String(opt.id) === String(value) ? 'active' : ''}`}
                  onClick={() => { onChange(opt.id); setQuery(opt.name); setOpen(false); }}
                >
                  {opt.name}
                </button>
              ))}
              {totalMatches > maxResults && (
                <div className="ss-more">
                  Showing top {maxResults} of {totalMatches} — keep typing to narrow down
                </div>
              )}
            </>
          )}
        </div>
      )}
      {error && <span className="error-message">{error}</span>}
    </div>
  );
};

export default SearchableSelect;